// Customize mode panel for the dashboard (DASHBOARD_WIDGETS_PLAN.md Phase 2).
// Renders a drag-and-drop-orderable list of active widgets (DASHBOARD_DND_AND_ACTIVITY_PLAN.md
// Task 1) with a remove control, plus a grouped catalog of disabled widgets that can be
// added back.

import {
  DndContext, closestCenter, KeyboardSensor, PointerSensor,
  useSensor, useSensors, type DragEndEvent,
} from "@dnd-kit/core";
import { restrictToVerticalAxis, restrictToParentElement } from "@dnd-kit/modifiers";
import {
  SortableContext, sortableKeyboardCoordinates, useSortable,
  verticalListSortingStrategy, arrayMove,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import type { DashboardWidgetPref } from "@medassist/shared";
import type { WidgetDef } from "./registry.js";
import { GripIcon, XIcon } from "../icons.js";

type WidgetGroup = "logging" | "clinical" | "analysis" | "timeline";

const GROUP_LABELS: Record<WidgetGroup, string> = {
  logging:  "Logging",
  clinical: "Clinical reference",
  analysis: "Analysis & output",
  timeline: "Timeline & search",
};

interface Props {
  /** Full ordered layout (enabled items first, disabled last). */
  layout: DashboardWidgetPref[];
  registry: WidgetDef[];
  onChange: (layout: DashboardWidgetPref[]) => void;
}

function SortableWidgetRow({ def, onRemove }: { def: WidgetDef; onRemove: () => void }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } =
    useSortable({ id: def.id });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    zIndex: isDragging ? 10 : undefined,
  };
  return (
    <li
      ref={setNodeRef}
      style={style}
      {...attributes}
      {...listeners}
      className="flex touch-none select-none items-center gap-2 rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 px-4 py-3 cursor-grab active:cursor-grabbing"
    >
      <GripIcon size={16} className="shrink-0 text-gray-300 dark:text-gray-600" />
      <span className="flex-1 text-sm font-medium text-gray-800 dark:text-gray-200">
        {def.title}
      </span>
      <button
        onPointerDown={(e) => e.stopPropagation()}
        onClick={onRemove}
        aria-label={`Remove ${def.title}`}
        className="rounded p-1 text-gray-400 hover:text-red-500 dark:hover:text-red-400"
      >
        <XIcon size={14} />
      </button>
    </li>
  );
}

export function CustomizePanel({ layout, registry, onChange }: Props) {
  const enabledItems = layout.filter((p) => p.enabled);
  const enabledSet   = new Set(enabledItems.map((p) => p.id));

  const enabledDefs = enabledItems
    .map((p) => registry.find((w) => w.id === p.id))
    .filter((w): w is WidgetDef => w != null);

  const disabledDefs = registry.filter((w) => !enabledSet.has(w.id));

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  function handleDragEnd(e: DragEndEvent) {
    const { active, over } = e;
    if (!over || active.id === over.id) return;
    const enabled  = layout.filter((p) => p.enabled);
    const disabled = layout.filter((p) => !p.enabled);
    const from = enabled.findIndex((p) => p.id === active.id);
    const to   = enabled.findIndex((p) => p.id === over.id);
    if (from < 0 || to < 0) return;
    onChange([...arrayMove(enabled, from, to), ...disabled]);
  }

  function remove(id: string) {
    const enabled  = layout.filter((p) => p.enabled && p.id !== id);
    const disabled = [
      ...layout.filter((p) => !p.enabled),
      { id, enabled: false },
    ];
    onChange([...enabled, ...disabled]);
  }

  function add(id: string) {
    const enabled  = [...layout.filter((p) => p.enabled), { id, enabled: true }];
    const disabled = layout.filter((p) => !p.enabled && p.id !== id);
    onChange([...enabled, ...disabled]);
  }

  const groups = (["logging", "clinical", "analysis", "timeline"] as WidgetGroup[])
    .map((key) => ({
      key,
      label: GROUP_LABELS[key],
      widgets: disabledDefs.filter((w) => w.group === key),
    }))
    .filter((g) => g.widgets.length > 0);

  return (
    <div className="space-y-6">
      {/* Active widgets — drag-and-drop-orderable list with a remove control */}
      <div>
        <h3 className="mb-3 text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">
          Active widgets
        </h3>
        {enabledDefs.length === 0 ? (
          <p className="text-sm italic text-gray-400 dark:text-gray-500">
            No widgets enabled — add some below.
          </p>
        ) : (
          <>
            <p className="mb-2 text-xs text-gray-400 dark:text-gray-500">
              Drag to reorder — or focus a row and use Space then the arrow keys.
            </p>
            <DndContext
              sensors={sensors}
              collisionDetection={closestCenter}
              modifiers={[restrictToVerticalAxis, restrictToParentElement]}
              onDragEnd={handleDragEnd}
            >
              <SortableContext items={enabledDefs.map((d) => d.id)} strategy={verticalListSortingStrategy}>
                <ol className="space-y-2">
                  {enabledDefs.map((def) => (
                    <SortableWidgetRow key={def.id} def={def} onRemove={() => remove(def.id)} />
                  ))}
                </ol>
              </SortableContext>
            </DndContext>
          </>
        )}
      </div>

      {/* Disabled widgets catalog — grouped by WidgetGroup */}
      {groups.length > 0 && (
        <div>
          <h3 className="mb-3 text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">
            Add widgets
          </h3>
          <div className="space-y-4">
            {groups.map((g) => (
              <div key={g.key}>
                <p className="mb-2 text-xs font-medium text-gray-400 dark:text-gray-500">
                  {g.label}
                </p>
                <div className="space-y-2">
                  {g.widgets.map((def) => (
                    <div
                      key={def.id}
                      className="flex items-center gap-3 rounded-xl border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50 px-4 py-3"
                    >
                      <span className="flex-1 text-sm text-gray-700 dark:text-gray-300">
                        {def.title}
                      </span>
                      <button
                        onClick={() => add(def.id)}
                        className="rounded-lg bg-(--accent) px-3 py-1 text-xs font-medium text-(--accent-contrast) hover:bg-(--accent-hover)"
                      >
                        Add
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
